import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PageHeader from "../../components/PageHeader";
import Loader from "../../components/Loader";
import VideoModal from "../../components/VideoModal";
import YOUTUBE_LINKS from "../../config/youtubeLinks";
import PersonalChannelOnboarding from "./PersonalChannelOnboarding";
import PersonalChannelStatus from "./PersonalChannelStatus";
import PersonalChannelRestrictedBanner from "./PersonalChannelRestrictedBanner";
import BrandedChannelCollapsedCTA from "./BrandedChannelCollapsedCTA";
import BrandedChannelExpanded from "./BrandedChannelExpanded";
import RoutingMatrix from "./RoutingMatrix";
import ChatSection from "./ChatSection";
import { deriveChannelsView, isChannelConnected } from "./channelHelpers";
import whatsappApi from "../../store/whatsappStore/whatsappApi";
import styles from "./whatsapp.module.scss";

const TAB_PERSONAL = "personal";
const TAB_BRANDED = "branded";

const WhatsApp = () => {
  const { pending, info } = useSelector((s) => s.branchReducer);
  const [tab, setTab] = useState(TAB_BRANDED);
  const { t } = useTranslation();

  const [wabaTemplates, setWabaTemplates] = useState([]);
  useEffect(() => {
    const id = info?._id || info?.id;
    if (tab === TAB_BRANDED && id) {
      whatsappApi
        .getTemplates(id)
        .then((r) => setWabaTemplates(r?.data?.templates || []))
        .catch(() => setWabaTemplates([]));
    }
  }, [tab, info]);

  if (pending) {
    return (
      <div className={styles.container} style={{ justifyContent: "center" }}>
        <Loader
          color="#73D389"
          style={{ marginTop: "100px", textAlign: "center" }}
        />
      </div>
    );
  }

  const branchID = info?._id || info?.id;
  const view = deriveChannelsView(info);
  const personalConnected = isChannelConnected(view.channel_2);
  const brandedConnected = isChannelConnected(view.channel_1);
  const showRouting = brandedConnected;

  const activeTab = tab;

  const handleDisconnect = async (slot) => {
    try {
      await whatsappApi.disconnectChannel(branchID, slot);
      window.location.reload();
    } catch (err) {
      console.error("disconnectChannel failed", err);
    }
  };

  const handleSaveRouting = async (routing) => {
    await whatsappApi.putRouting(branchID, routing);
  };

  return (
    <div className={styles.container}>
      <PageHeader title={t("NSWhatsapp.title")}>
        <VideoModal url={YOUTUBE_LINKS.Whatsapp} />
      </PageHeader>

      <div className={styles.tabBar}>
        <button
          type="button"
          className={`${styles.tab} ${
            activeTab === TAB_PERSONAL ? styles.tabActive : ""
          }`}
          onClick={() => setTab(TAB_PERSONAL)}
        >
          {t("NSWhatsapp.channelPersonal")}
        </button>
        <button
          type="button"
          className={`${styles.tab} ${
            activeTab === TAB_BRANDED ? styles.tabActive : ""
          }`}
          onClick={() => setTab(TAB_BRANDED)}
        >
          {t("NSWhatsapp.channelBranded")}
        </button>
      </div>

      {activeTab === TAB_PERSONAL && (
        <section className={styles.personalSection}>
          <PersonalChannelRestrictedBanner branchID={branchID} branchName={info?.name} />
          {personalConnected ? (
            <PersonalChannelStatus
              channel={view.channel_2}
              onDisconnect={
                view.legacyShim ? null : () => handleDisconnect("channel_2")
              }
            />
          ) : (
            <PersonalChannelOnboarding info={info} />
          )}
        </section>
      )}

      {activeTab === TAB_BRANDED && (
        <section className={styles.brandedSection}>
          {brandedConnected ? (
            <>
              <BrandedChannelExpanded
                channel={view.channel_1}
                info={info}
                liveTemplates={wabaTemplates}
                onDisconnect={
                  view.legacyShim ? null : () => handleDisconnect("channel_1")
                }
              />
              <ChatSection channels={view} />
            </>
          ) : (
            <BrandedChannelCollapsedCTA info={info} />
          )}
        </section>
      )}

      {showRouting && (
        <section className={styles.routingSection}>
          <RoutingMatrix
            channels={view}
            routing={info?.configuration?.whatsapp_provider?.routing}
            onSave={handleSaveRouting}
          />
        </section>
      )}
    </div>
  );
};

export default WhatsApp;
